import { type MaybeRefOrGetter, toValue } from '@vueuse/core';
import { computed, type ComputedRef, ref, unref } from 'vue';

export const ModalCancelBehavior = {
    NoReturn: 0,
    Resolve: 1,
    Reject: 2,
} as const;

export type ModalCancelBehaviorType = typeof ModalCancelBehavior[keyof typeof ModalCancelBehavior];

export type ModalDialogOptions = {
    onCancel?: ModalCancelBehaviorType;
    // may throw exception or return falsy
    onBeforeSubmit?: () => (Promise<unknown> | unknown);
};

export type CustomModalDialogOptions<R = void> = {
    onCancel?: ModalCancelBehaviorType;
    // may throw exception or return falsy
    onBeforeSubmit?: (result: R | undefined) => (Promise<R> | R);
};

export type ModalDialogApi = {
    showModal: (options?: ModalDialogOptions | null | undefined) => Promise<void>;
    readonly isSubmitted: ComputedRef<boolean>;
    toggleModal(show?: boolean): Promise<void>;
    requestSubmit: () => void;
};

export type CustomModalDialogApi<T extends unknown[] = [], R = void> = {
    showModal: (...args: T) => Promise<R>;
    showModalEx: (options?: CustomModalDialogOptions<R>, ...args: T) => Promise<R>;
    readonly isSubmitted: ComputedRef<boolean>;
};

export type CustomModalCallback<S extends unknown[] = [], R = void> = (...args: S) => (Promise<R> | R);

export function useExposeCustomModalDialogApi<T extends unknown[] = [], R = void>({
    api,
    onBeforeShow,
    onBeforeSubmit,
}: {
    api?: MaybeRefOrGetter<ModalDialogApi | undefined>;
    onBeforeShow?: CustomModalCallback<T, void>;
    onBeforeSubmit?: CustomModalCallback<T, R>;
}) {
    const apiRef = ref<ModalDialogApi | undefined>();
    const apiGetter = () => {
        const api0 = api ? toValue(api) ?? unref(apiRef) : unref(apiRef);
        return api0;
    };

    const apiExpose: CustomModalDialogApi<T, R> = {
        isSubmitted: computed(() => {
            const _api = apiGetter();
            return unref(_api?.isSubmitted) ?? false;
        }),
        showModal: (...args) => apiExpose.showModalEx(undefined, ...args),
        showModalEx: (options, ...args) => {
            const _api = apiGetter();
            if(!_api) {
                return Promise.reject(new Error('no api'));
            }
            let submitResult: R | undefined;
            return Promise.resolve()
                .then(() => onBeforeShow?.(...args))
                .then(() => _api.showModal({
                    ...options,
                    onBeforeSubmit: () => {
                        const promise = Promise.resolve()
                            .then(() => onBeforeSubmit?.(...args))
                            .then((_submitResult) => options?.onBeforeSubmit?.(_submitResult) ?? _submitResult)
                            .then((_submitResult) => { submitResult = _submitResult; });
                        return promise;
                    },
                }))
                .then(() => submitResult!);
        },
    };

    return [
        apiExpose,
        apiRef,
    ] as const;
}

export function useCustomModalDialogApi<T extends unknown[] = [], R = void>(
    api?: MaybeRefOrGetter<CustomModalDialogApi<T, R> | undefined>,
) {
    const apiRef = ref<CustomModalDialogApi<T, R> | undefined>();
    const apiGetter = () => {
        const api0 = api ? toValue(api) ?? unref(apiRef) : unref(apiRef);
        return api0;
    };

    const apiExpose: CustomModalDialogApi<T, R> = {
        isSubmitted: computed(() => {
            const _api = apiGetter();
            return unref(_api?.isSubmitted) ?? false;
        }),
        showModal: (...args) => {
            const _api = apiGetter();
            if(!_api) {
                return Promise.reject(new Error('no api'));
            }
            return _api.showModal(...args);
        },
        showModalEx: (...args) => {
            const _api = apiGetter();
            if(!_api) {
                return Promise.reject(new Error('no api'));
            }
            return _api.showModalEx(...args);
        },
    };

    return [
        apiExpose,
        apiRef,
    ] as const;
}
